import React, { useEffect, useState, useCallback } from 'react';

import { MinusCircleOutlined, PlusOutlined, CalculatorOutlined } from '@ant-design/icons';
import { Form, InputNumber, Select, DatePicker, Modal, Row, Col, Button, Tabs, Divider, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { operatorApiFetch } from '@services/operator/apiService';
import { rateCardService } from '@services/operator/rateCardService';

import StoragePreviewResult from './StoragePreviewResult';
import { STORAGE_BILLING_MODE_OPTIONS } from './storageBillingModes';

const { Text } = Typography;

const FEE_SECTIONS = {
  inbound: {
    title: 'fee_sections.inbound',
    fields: [
      { key: 'inbound_fee_base', label: 'fields.inbound_fee_base' },
      { key: 'inbound_fee_per_unit', label: 'fields.inbound_fee_per_unit' },
      { key: 'inbound_fee_per_box', label: 'fields.inbound_fee_per_box' },
      { key: 'inbound_fee_per_pallet', label: 'fields.inbound_fee_per_pallet' },
    ],
  },
  picking: {
    title: 'fee_sections.picking',
    fields: [
      { key: 'picking_fee_per_item', label: 'fields.picking_fee_per_item' },
      { key: 'picking_fee_box', label: 'fields.picking_fee_box' },
      { key: 'picking_fee_pallet', label: 'fields.picking_fee_pallet' },
    ],
  },
  storage: {
    title: 'fee_sections.storage',
    fields: [
      { key: 'price_per_pallet', label: 'fields.price_per_pallet' },
      { key: 'price_per_cuft', label: 'fields.price_per_cuft' },
      { key: 'price_per_kg', label: 'fields.price_per_kg' },
      { key: 'price_per_pcs', label: 'fields.price_per_pcs' },
    ],
  },
  other: {
    title: 'fee_sections.other',
    fields: [
      { key: 'order_document_fee', label: 'fields.order_document_fee' },
      { key: 'return_verify_fee', label: 'fields.return_verify_fee' },
      { key: 'return_document_fee', label: 'fields.return_document_fee' },
    ],
  },
};

// 計費模式 → 唯一生效的單價欄位。刻意保留全部單一單價模式（非只下拉那 4 種）：
// 編輯舊卡（by_total_pallet/by_exact_volume/by_pcs）才能 render 正確欄位、submit 不誤清生效單價。
const STORAGE_MODE_TO_FIELD = {
  by_sku_pallet: 'price_per_pallet',
  by_total_pallet: 'price_per_pallet',
  by_exact_volume: 'price_per_cuft',
  by_weight: 'price_per_kg',
  by_pcs: 'price_per_pcs',
};

// 沒有單一單價欄位、submit 前 4 個 storage 單價都要清掉的模式（價格走 shelf_prices 或不計費）
const STORAGE_MODES_WITHOUT_PRICE_FIELD = ['by_shelf', 'none'];

// 切換 mode 後，非 active 的 storage 單價欄位殘留值要在 submit 前清掉，避免髒資料進 DB
const ALL_STORAGE_PRICE_FIELDS = ['price_per_pallet', 'price_per_cuft', 'price_per_kg', 'price_per_pcs'];

// effective_from 不可選過去日期：費率卡是「排程未來費率」（editable? 僅限 future），
// 回溯生效會改動已結算的歷史帳，且建立後立即不可編輯。今天仍可選（與預設值一致）。
const isPastDate = (current) => current && current < dayjs().startOf('day');

const RateCardForm = ({ open, onCancel, onSubmit, initialValues, loading, isEdit, merchantId }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const prefix = 'operator.merchants.show';
  const rcPrefix = 'operator.rate_cards';
  const [shelfTypes, setShelfTypes] = useState([]);
  const billingMode = Form.useWatch('storage_billing_mode', form);
  // by_shelf：一張卡每個儲位類型只能有一筆價（後端 unique index）。下拉排除其他列已選的類型，
  // 讓「選過的選不到」，不用等存檔才被後端擋。保留自己這列當前的選擇。
  const shelfPriceRows = Form.useWatch('rate_card_shelf_prices_attributes', form);
  const shelfTypesForRow = (rowName) => {
    const takenByOtherRows = new Set(
      (shelfPriceRows || [])
        .map((row, idx) => (idx === rowName ? undefined : row?.shelf_type_id))
        .filter(id => id !== undefined && id !== null)
    );
    return shelfTypes.filter(st => !takenByOtherRows.has(st.id));
  };
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const fetchShelfTypes = useCallback(async () => {
    if (!merchantId) return;
    try {
      const response = await operatorApiFetch(`shelf_types`);
      const data = await response.json();
      if (data.success) {
        // by_shelf 只計 role=storage 的儲位；system-reserved 類型（暫存區/驗貨區/揀貨車/虛擬池）
        // 永遠不會被計費，列進單價下拉只會讓 OP 設了卻不生效，故濾掉
        setShelfTypes((data.data || []).filter(st => !st.system_reserved));
      }
    } catch (error) {
      console.error('Failed to fetch shelf types:', error);
    }
  }, [merchantId]);

  useEffect(() => {
    if (open) fetchShelfTypes();
  }, [open, fetchShelfTypes]);

  useEffect(() => {
    // 開窗/重開時清掉上一次的試算結果，避免同模式換卡時殘留舊金額
    setPreviewResult(null);
    setPreviewError(null);
    if (open && initialValues) {
      const formValues = {
        ...initialValues,
        effective_from: initialValues.effective_from ? dayjs(initialValues.effective_from) : dayjs(),
      };
      if (initialValues.shelf_prices?.length > 0) {
        formValues.rate_card_shelf_prices_attributes = initialValues.shelf_prices.map(sp => ({
          // 只有編輯既有卡才帶 id（nested update）；建立新版本是 clone，舊卡的 shelf_price id 不可帶
          ...(isEdit ? { id: sp.id } : {}),
          shelf_type_id: sp.shelf_type_id,
          price: sp.price,
        }));
      }
      form.setFieldsValue(formValues);
    } else if (open) {
      form.resetFields();
    }
  }, [open, initialValues, isEdit]);

  // 計費模式切換後，先前的試算結果已失效，清掉避免誤導
  useEffect(() => {
    setPreviewResult(null);
    setPreviewError(null);
  }, [billingMode]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const mode = values.storage_billing_mode;
      // 清掉非 active 的殘留單價避免髒資料；by_shelf/none 無單一欄位故全清。
      // 未知 mode 保險不清，免得未來加 mode 漏補 STORAGE_MODE_TO_FIELD 時誤清生效單價。
      const activeField = STORAGE_MODE_TO_FIELD[mode];
      const shouldClearPrices = activeField || STORAGE_MODES_WITHOUT_PRICE_FIELD.includes(mode);
      const clearedPrices = shouldClearPrices
        ? ALL_STORAGE_PRICE_FIELDS.reduce((acc, field) => {
            if (field !== activeField) acc[field] = null;
            return acc;
          }, {})
        : {};
      const submitData = {
        ...values,
        ...clearedPrices,
        effective_from: values.effective_from?.format('YYYY-MM-DD'),
      };
      onSubmit(submitData);
    } catch {
      // validation failed
    }
  };

  // 單價依當前 mode 動態帶對應欄位（含 by_pcs/by_exact_volume 舊模式），避免漏送誤判 $0。
  const handlePreview = async () => {
    if (!merchantId) return;
    const values = form.getFieldsValue();
    const activeField = STORAGE_MODE_TO_FIELD[values.storage_billing_mode];
    const rateCard = {
      storage_billing_mode: values.storage_billing_mode,
      ...(activeField ? { [activeField]: values[activeField] } : {}),
      rate_card_shelf_prices_attributes: (values.rate_card_shelf_prices_attributes || []).map(sp => ({
        shelf_type_id: sp.shelf_type_id,
        price: sp.price,
      })),
    };

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await rateCardService.preview(merchantId, rateCard);
      if (data.success) {
        setPreviewResult(data.data);
      } else {
        setPreviewError(t(`${prefix}.preview.error`));
      }
    } catch (err) {
      console.error('[rate card preview] failed:', err);
      setPreviewError(t(`${prefix}.preview.error`));
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderFeeSection = (sectionKey) => {
    const section = FEE_SECTIONS[sectionKey];
    return (
      <div style={{ padding: '12px 0' }}>
        <Row gutter={[24, 0]}>
          {section.fields.map(field => (
            <Col span={12} key={field.key}>
              <Form.Item 
                name={field.key} 
                label={t(`${prefix}.${field.label}`)}
                initialValue={0}
              >
                <InputNumber 
                  min={0} 
                  precision={2} 
                  style={{ width: '100%' }} 
                  prefix="$" 
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderStorageField = (fieldKey) => {
    const field = FEE_SECTIONS.storage.fields.find(f => f.key === fieldKey);
    if (!field) return null;
    return (
      <div style={{ padding: '12px 0' }}>
        <Row gutter={[24, 0]}>
          <Col span={12} key={field.key}>
            <Form.Item
              name={field.key}
              label={t(`${prefix}.${field.label}`)}
              initialValue={0}
              // 計費模式 active 單價必須 > 0，否則倉租算成 $0（要免費請改選「不計費」）
              rules={[{
                validator: (_, value) => (
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t(`${rcPrefix}.validation.storage_price_required`)))
                ),
              }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                prefix="$"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  // storage tab 內：試算按鈕 + 結果/錯誤顯示（結果緊接按鈕下方）
  const renderPreviewSection = () => (
    <div style={{ marginTop: 16 }}>
      <Button
        type="primary"
        ghost
        icon={<CalculatorOutlined />}
        onClick={handlePreview}
        loading={previewLoading}
        disabled={!merchantId}
      >
        {t(`${prefix}.actions.calculate`)}
      </Button>
      {previewError && (
        <div style={{ marginTop: 8, color: '#cf1322' }}>{previewError}</div>
      )}
      {previewResult && !previewError && <StoragePreviewResult result={previewResult} />}
    </div>
  );

  const tabItems = [
    {
      key: 'storage',
      label: t(`${prefix}.fee_sections.storage`),
      children: (
        <div style={{ padding: '12px 0' }}>
          <Form.Item
            name="storage_billing_mode"
            label={t(`${prefix}.fields.storage_billing_mode`)}
            rules={[{ required: true, message: t('common.validation.required') }]}
            extra={billingMode && (
              <div style={{ marginTop: 8, fontSize: '12px', lineHeight: '1.6', color: '#666', backgroundColor: '#e6f7ff', padding: '8px 12px', borderRadius: '4px', border: '1px solid #91d5ff' }}>
                <div style={{ fontWeight: 'bold', color: '#0050b3', marginBottom: 2 }}>
                  {t(`${prefix}.billing_modes.${billingMode}`)}
                </div>
                <div>{t(`${prefix}.billing_mode_details.${billingMode}.desc`)}</div>
                <div style={{ fontStyle: 'italic', marginTop: 2 }}>{t(`${prefix}.billing_mode_details.${billingMode}.example`)}</div>
                {t(`${prefix}.billing_mode_details.${billingMode}.required`) !== '無' && (
                  <div style={{ marginTop: 4, color: '#cf1322' }}>
                    <strong>{t(`${prefix}.fields.required_field`)}:</strong> {t(`${prefix}.billing_mode_details.${billingMode}.required`)}
                  </div>
                )}
              </div>
            )}
          >
            <Select>
              {STORAGE_BILLING_MODE_OPTIONS.map(mode => (
                <Select.Option key={mode} value={mode}>
                  {t(`${prefix}.billing_modes.${mode}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider style={{ margin: '24px 0 16px 0' }} />

          {billingMode === 'by_shelf' && (
            <div style={{ marginTop: 0 }}>
              <Divider orientation="left" style={{ margin: '0 0 12px 0' }}>
                <Text strong>{t(`${prefix}.fee_sections.shelf_prices`)}</Text>
              </Divider>
                          <Form.List 
                            name="rate_card_shelf_prices_attributes"
                            rules={[
                              {
                                validator: async (_, names) => {
                                  if (billingMode === 'by_shelf' && (!names || names.length < 1)) {
                                    return Promise.reject(new Error(t(`${rcPrefix}.validation.at_least_one_shelf_price`)));
                                  }
                                },
                              },
                            ]}
                          >
                            {(fields, { add, remove }, { errors }) => (
                              <div style={{ backgroundColor: '#fafafa', padding: 12, borderRadius: 8 }}>
                                <Row gutter={16} style={{ marginBottom: 4, paddingLeft: 8 }}>
                                  <Col span={11}><Text type="secondary" size="small">{t(`${prefix}.fields.shelf_type`)}</Text></Col>
                                  <Col span={10}><Text type="secondary" size="small">{t(`${prefix}.fields.price`)}</Text></Col>
                                </Row>
                                {fields.map(({ key, name, ...restField }) => (
                                  <Row gutter={12} key={key} align="top">
                                    <Col span={11}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'shelf_type_id']}
                                        rules={[{ required: true, message: t('common.validation.required') }]}
                                      >
                                        <Select placeholder={t(`${prefix}.fields.shelf_type`)} style={{ width: '100%' }}>
                                          {shelfTypesForRow(name).map(st => (
                                            <Select.Option key={st.id} value={st.id}>{st.name}</Select.Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                    <Col span={10}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'price']}
                                        // 加了列就必須 > 0；不收費請移除此列（涵蓋留空與填 0）
                                        rules={[{
                                          validator: (_, value) => (
                                            Number(value) > 0
                                              ? Promise.resolve()
                                              : Promise.reject(new Error(t(`${rcPrefix}.validation.shelf_price_positive`)))
                                          ),
                                        }]}
                                      >
                                        <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" placeholder={t(`${prefix}.fields.price`)} />
                                      </Form.Item>
                                    </Col>
                                    <Col span={3} style={{ textAlign: 'right', paddingTop: 5 }}>
                                      <Button 
                                        type="text" 
                                        danger 
                                        icon={<MinusCircleOutlined />} 
                                        onClick={() => remove(name)} 
                                      />
                                    </Col>
                                    <Form.Item name={[name, 'id']} hidden><input /></Form.Item>
                                  </Row>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                                  {t(`${prefix}.actions.add_shelf_price`)}
                                </Button>
                                <Form.ErrorList errors={errors} />
                              </div>
                            )}
                          </Form.List>            </div>
          )}

          {STORAGE_MODE_TO_FIELD[billingMode] && (
            <>
              <Divider orientation="left" style={{ margin: '0 0 12px 0' }}>
                <Text strong>{t(`${prefix}.fee_sections.storage`)}</Text>
              </Divider>
              {renderStorageField(STORAGE_MODE_TO_FIELD[billingMode])}
            </>
          )}

          {billingMode === 'none' && (
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <Text type="secondary">{t(`${prefix}.billing_modes.none_desc`, { defaultValue: 'No billing for storage' })}</Text>
            </div>
          )}

          {billingMode && billingMode !== 'none' && renderPreviewSection()}
        </div>
      ),
    },
    {
      key: 'inbound',
      label: t(`${prefix}.fee_sections.inbound`),
      children: renderFeeSection('inbound'),
    },
    {
      key: 'picking',
      label: t(`${prefix}.fee_sections.picking`),
      children: renderFeeSection('picking'),
    },
    {
      key: 'other',
      label: t(`${prefix}.fee_sections.other`),
      children: renderFeeSection('other'),
    },
  ];

  return (
    <Modal
      open={open}
      title={isEdit ? t(`${rcPrefix}.form.edit_title`) : t(`${rcPrefix}.form.create_title`)}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="effective_from"
              label={t(`${rcPrefix}.fields.effective_from`)}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <DatePicker style={{ width: '100%' }} disabledDate={isPastDate} />
            </Form.Item>
          </Col>
        </Row>
        
        <Divider style={{ margin: '8px 0 16px 0' }} />
        
        <Tabs items={tabItems} defaultActiveKey="storage" />
      </Form>
    </Modal>
  );
};

export default RateCardForm;

